import { useListState } from "@/hooks/use-list-state";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Search, Shield } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PaginationControls } from "@/components/pagination-controls";
import { CombinedImpersonationEntry, ImpersonationLogEntry, ImpersonationSortField, SortDir, VendorImpersonationLogEntry } from "./types";
import { downloadCsv, formatLogDate, formatLogTime, impersonationDuration, rowsToCsv } from "./helpers";

export function ImpersonationLogPanel() {
  const [searchTerm, setSearchTerm] = useListState("impersonation-log:search", "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortField, setSortField] = useListState<ImpersonationSortField>("impersonation-log:sort", "startedAt");
  const [sortDir, setSortDir] = useListState<SortDir>("impersonation-log:direction", "desc");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const logQuery = useQuery<{ entries: ImpersonationLogEntry[] }>({
    queryKey: ["/api/admin/impersonation-log"],
    queryFn: async () => {
      const res = await fetch("/api/admin/impersonation-log", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const vendorLogQuery = useQuery<{ entries: VendorImpersonationLogEntry[] }>({
    queryKey: ["/api/admin/vendor-impersonation-log"],
    queryFn: async () => {
      const res = await fetch("/api/admin/vendor-impersonation-log", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const userEntries: CombinedImpersonationEntry[] = (logQuery.data?.entries ?? []).map((e) => ({
    kind: "user",
    rowKey: `user-${e.id}`,
    id: e.id,
    impersonatorEmail: e.impersonatorEmail,
    targetEmail: e.targetEmail,
    targetVendorName: null,
    teamMemberName: null,
    startedAt: e.startedAt,
    endedAt: e.endedAt,
    actionsTaken: e.actionsTaken,
    ipAddress: e.ipAddress,
    userAgent: e.userAgent,
  }));

  const vendorEntries: CombinedImpersonationEntry[] = (vendorLogQuery.data?.entries ?? []).map((e) => ({
    kind: "vendor",
    rowKey: `vendor-${e.id}`,
    id: e.id,
    impersonatorEmail: e.impersonatorEmail,
    targetEmail: e.teamMemberName
      ? `${e.targetVendorName} — ${e.teamMemberName}`
      : e.targetVendorName,
    targetVendorName: e.targetVendorName,
    teamMemberName: e.teamMemberName,
    startedAt: e.startedAt,
    endedAt: e.endedAt,
    actionsTaken: e.actionsTaken ?? [],
    ipAddress: e.ipAddress,
    userAgent: e.userAgent,
  }));

  const entries: CombinedImpersonationEntry[] = [...userEntries, ...vendorEntries];

  const filtered = entries.filter((e) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (
        !String(e.impersonatorEmail ?? "").toLowerCase().includes(q) &&
        !String(e.targetEmail ?? "").toLowerCase().includes(q) &&
        !(e.ipAddress && e.ipAddress.includes(q))
      ) {
        return false;
      }
    }
    if (dateFrom) {
      const fromMs = new Date(dateFrom).getTime();
      if (new Date(e.startedAt).getTime() < fromMs) return false;
    }
    if (dateTo) {
      const toMs = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000;
      if (new Date(e.startedAt).getTime() >= toMs) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortField === "startedAt") {
      cmp = new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
    } else if (sortField === "impersonatorEmail") {
      cmp = a.impersonatorEmail.localeCompare(b.impersonatorEmail);
    } else if (sortField === "targetEmail") {
      cmp = a.targetEmail.localeCompare(b.targetEmail);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = sorted.slice(start, start + pageSize);

  useEffect(() => {
    setPage(1);
    setExpandedId(null);
  }, [searchTerm, dateFrom, dateTo, sortField, sortDir]);

  const handleSort = (field: ImpersonationSortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "startedAt" ? "desc" : "asc");
    }
  };

  const SortIcon = ({ field }: { field: ImpersonationSortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    return sortDir === "asc"
      ? <ArrowUp className="h-3.5 w-3.5 ml-1" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1" />;
  };

  const handleDownloadCsv = () => {
    const header = [
      "Session Type",
      "Impersonator Email",
      "Target",
      "Start Time",
      "End Time",
      "Duration",
      "Action Count",
      "IP Address",
    ];
    const rows: Array<Array<unknown>> = [header];
    for (const e of sorted) {
      rows.push([
        e.kind === "vendor" ? "Vendor" : "User",
        e.impersonatorEmail,
        e.targetEmail,
        new Date(e.startedAt).toISOString(),
        e.endedAt ? new Date(e.endedAt).toISOString() : "",
        e.endedAt ? impersonationDuration(e.startedAt, e.endedAt) : "active",
        e.actionsTaken.length,
        e.ipAddress ?? "",
      ]);
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`impersonation-audit-log-${stamp}.csv`, rowsToCsv(rows));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2" data-testid="heading-impersonation-log" data-tour="admin-impersonation-log-heading">
              <Shield className="h-5 w-5" />
              Impersonation Audit Log
            </CardTitle>
            <CardDescription>
              Read-only audit trail of all impersonation sessions. {entries.length} total session{entries.length !== 1 ? "s" : ""}.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by email or IP..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-[260px] h-9"
                data-testid="input-impersonation-search"
                data-tour="admin-impersonation-log-search"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCsv}
              disabled={sorted.length === 0}
              data-testid="button-download-impersonation-csv"
            >
              <Download className="h-4 w-4 mr-1" />
              Download CSV
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap mt-2">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">From:</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-[160px] text-xs"
              data-testid="input-impersonation-date-from"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">To:</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-[160px] text-xs"
              data-testid="input-impersonation-date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              data-testid="button-clear-date-filter"
            >
              Clear dates
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {(logQuery.isLoading || vendorLogQuery.isLoading) ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : logQuery.isError ? (
          <div className="p-6 text-sm text-destructive" data-testid="text-impersonation-log-error">
            Failed to load impersonation log.
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-12 text-center">
            <Shield className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">{(searchTerm || dateFrom || dateTo) ? "No sessions match your filter." : "No impersonation sessions recorded."}</p>
            {vendorLogQuery.isError && (
              <p className="mt-2 text-xs text-amber-700" data-testid="text-vendor-impersonation-log-warning">
                Vendor impersonation sessions could not be loaded.
              </p>
            )}
          </div>
        ) : (
          <>
            {vendorLogQuery.isError && (
              <div
                className="px-4 py-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800"
                data-testid="text-vendor-impersonation-log-warning"
              >
                Vendor impersonation sessions could not be loaded — showing user sessions only.
              </div>
            )}
            <div className="overflow-x-auto">
              <Table data-tour="admin-impersonation-log-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        className="flex items-center text-xs font-medium"
                        onClick={() => handleSort("impersonatorEmail")}
                        data-testid="sort-impersonator"
                      >
                        Impersonator
                        <SortIcon field="impersonatorEmail" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        className="flex items-center text-xs font-medium"
                        onClick={() => handleSort("targetEmail")}
                        data-testid="sort-target"
                      >
                        Target User
                        <SortIcon field="targetEmail" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        className="flex items-center text-xs font-medium"
                        onClick={() => handleSort("startedAt")}
                        data-testid="sort-started"
                      >
                        Start Time
                        <SortIcon field="startedAt" />
                      </button>
                    </TableHead>
                    <TableHead>End Time</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((entry) => {
                    const isActive = !entry.endedAt;
                    const isExpanded = expandedId === entry.rowKey;
                    const isVendor = entry.kind === "vendor";
                    return (
                      <TableRow
                        key={entry.rowKey}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setExpandedId(isExpanded ? null : entry.rowKey)}
                        data-testid={`row-impersonation-${entry.rowKey}`}
                      >
                        <TableCell className="font-medium text-sm" data-testid={`text-impersonator-${entry.rowKey}`}>
                          {entry.impersonatorEmail}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`text-target-${entry.rowKey}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            {isVendor && (
                              <Badge
                                variant="outline"
                                className="bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 text-[10px] uppercase tracking-wide"
                                data-testid={`badge-vendor-${entry.rowKey}`}
                              >
                                Vendor
                              </Badge>
                            )}
                            <span>{entry.targetEmail}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap" data-testid={`text-start-${entry.rowKey}`}>
                          {formatLogDate(entry.startedAt)}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {isActive ? (
                            <Badge className="bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800 text-xs" data-testid={`badge-active-${entry.rowKey}`}>
                              Active
                            </Badge>
                          ) : (
                            <span data-testid={`text-end-${entry.rowKey}`}>{formatLogDate(entry.endedAt!)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm" data-testid={`text-actions-count-${entry.rowKey}`}>
                          {isVendor ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            entry.actionsTaken.length
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground font-mono" data-testid={`text-ip-${entry.rowKey}`}>
                          {entry.ipAddress || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {expandedId !== null && (() => {
              const entry = entries.find((e) => e.rowKey === expandedId);
              if (!entry) return null;
              const isVendor = entry.kind === "vendor";
              return (
                <div className="border-t bg-muted/30 p-4 space-y-3" data-testid={`detail-impersonation-${entry.rowKey}`}>
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      Session Details
                      {isVendor && (
                        <Badge
                          variant="outline"
                          className="bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 text-[10px] uppercase tracking-wide"
                          data-testid={`badge-vendor-detail-${entry.rowKey}`}
                        >
                          Vendor
                        </Badge>
                      )}
                    </h4>
                    <Badge variant="outline" className="text-xs">
                      Duration: {impersonationDuration(entry.startedAt, entry.endedAt)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground">Impersonator:</span>
                      <div className="font-medium">{entry.impersonatorEmail}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Target:</span>
                      <div className="font-medium">{entry.targetEmail}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">IP Address:</span>
                      <div className="font-medium font-mono">{entry.ipAddress || "—"}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">User Agent:</span>
                      <div className="font-medium break-all text-[11px] leading-tight">{entry.userAgent || "—"}</div>
                    </div>
                  </div>

                  {entry.actionsTaken.length > 0 ? (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1.5">
                        Actions taken ({entry.actionsTaken.length}):
                      </div>
                      <div className="rounded border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs py-1.5">Method</TableHead>
                              <TableHead className="text-xs py-1.5">Action</TableHead>
                              <TableHead className="text-xs py-1.5">Status</TableHead>
                              <TableHead className="text-xs py-1.5 text-right">Time</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {entry.actionsTaken.map((a, i) => (
                              <TableRow key={i} data-testid={`row-action-${entry.id}-${i}`}>
                                <TableCell className="py-1 text-xs font-mono font-semibold">{a.method}</TableCell>
                                <TableCell className="py-1 text-xs max-w-[400px]">
                                  <div className="truncate">{a.summary || `${a.method} ${a.endpoint}`}</div>
                                  <div className="font-mono text-[10px] text-muted-foreground truncate">{a.endpoint}</div>
                                </TableCell>
                                <TableCell className="py-1 text-xs">
                                  {typeof a.status === "number" ? (
                                    <Badge
                                      variant={a.status < 400 ? "secondary" : "destructive"}
                                      className="text-[10px] px-1.5 py-0"
                                      data-testid={`badge-action-status-${entry.id}-${i}`}
                                    >
                                      {a.status}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="py-1 text-xs text-muted-foreground text-right whitespace-nowrap">
                                  {formatLogTime(a.timestamp)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">
                      No write actions taken during this session.
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="p-3 border-t">
              <PaginationControls
                page={safePage}
                pageSize={pageSize}
                total={sorted.length}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                testIdPrefix="impersonation-log"
                itemLabel="sessions"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
